import { NavLink, useLocation } from 'react-router-dom';
import { ItemBloqueado } from '@/components/patterns/permission-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { navegacao } from '@/navigation';
import { cn } from '@/lib/utils';

/**
 * Sidebar de 240px expandida / 68px colapsada.
 *
 * Lista plana dos sete módulos. O item ativo é superfície azul cheia com texto
 * `on-primary`, como no sustenta-ai-app: o azul primário entra em ação, link,
 * foco e **estado ativo** — este é o estado ativo.
 *
 * Duas decisões vêm de bugs reais registrados no playbook:
 *  1. a lista de módulos ROLA — `min-h-0 flex-1 overflow-y-auto` — e o rodapé é
 *     irmão fixo dela, nunca `mt-auto` dentro da lista;
 *  2. item bloqueado fica visível com cadeado e motivo, nunca some.
 */
export function Sidebar({
  colapsada,
  aoNavegar,
}: {
  colapsada: boolean;
  aoNavegar?: () => void;
}) {
  const itemBase =
    'flex min-h-11 items-center gap-3 rounded-button px-3 py-2 text-body transition-colors duration-fast ease-standard';

  const itens = navegacao;
  const local = useLocation();

  return (
    <div className="flex h-full flex-col bg-bg-surface">
      {/* 1. lista rolável */}
      <nav aria-label="Módulos do PAN" className="pan-scroll-area min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <ul className="flex flex-col gap-1">
          {itens.map((item) => {
            const Icone = item.icone;

            return (
              <li key={item.id}>
                {/* 2. bloqueio é visível, não silencioso */}
                {item.bloqueado ? (
                  <ItemBloqueado
                    rotulo={colapsada ? '' : item.rotulo}
                    motivo={item.bloqueado}
                    icone={<Icone className="size-[18px] shrink-0" aria-hidden="true" />}
                  />
                ) : colapsada ? (
                  // `NavLink` calcula `isActive` sozinho quando `className` é
                  // função — mas o `Slot` do Radix (`TooltipTrigger asChild`)
                  // não entende função de `className`, só string: repassa o
                  // CÓDIGO-FONTE da função como classe literal (bug real,
                  // visto em produção — todo ícone da sidebar recolhida some,
                  // porque nenhuma classe de fato é aplicada). Por isso o
                  // `isActive` é calculado aqui fora, e a classe já entra pronta.
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={item.rota}
                        onClick={aoNavegar}
                        className={cn(
                          itemBase,
                          'justify-center',
                          local.pathname.startsWith(item.rota)
                            ? 'bg-primary text-on-primary shadow-rest'
                            : 'text-on-surface hover:bg-bg-subtle',
                        )}
                      >
                        <Icone className="size-[18px] shrink-0" aria-hidden="true" />
                        <span className="sr-only">{item.rotulo}</span>
                      </NavLink>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <span className="font-bold">{item.rotulo}</span> · {item.descricao}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <NavLink
                    to={item.rota}
                    onClick={aoNavegar}
                    className={({ isActive }) =>
                      cn(
                        itemBase,
                        isActive
                          ? 'bg-primary font-bold text-on-primary shadow-rest'
                          : 'text-on-surface hover:bg-bg-subtle',
                      )
                    }
                  >
                    <Icone className="size-[18px] shrink-0" aria-hidden="true" />
                    <span className="flex-1 truncate">{item.rotulo}</span>
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
